import { useState, useEffect } from 'react'
import { supabase } from '@/lib/supabase'

export interface RecentSearchItem {
    id: string
    type: 'pick' | 'collection' | 'folder'
    data: any
}

const RECENT_SEARCHES_KEY = 'shuvo_recent_searches'
const MAX_RECENT_SEARCHES = 10

export function useRecentSearches() {
    const [recentSearches, setRecentSearches] = useState<RecentSearchItem[]>([])

    useEffect(() => {
        let isVerified = false
        const load = async () => {
            try {
                const saved = localStorage.getItem(RECENT_SEARCHES_KEY)
                if (saved) {
                    let parsed = JSON.parse(saved) as RecentSearchItem[]
                    setRecentSearches(parsed)
                    
                    if (!isVerified && parsed.length > 0) {
                        isVerified = true
                        
                        const pickIds = parsed.filter(s => s.type === 'pick').map(s => s.id)
                        const collectionIds = parsed.filter(s => s.type === 'collection').map(s => s.id)
                        const folderIds = parsed.filter(s => s.type === 'folder').map(s => s.id)

                        const promises = []
                        if (pickIds.length > 0) promises.push(supabase.from('picks').select('id').in('id', pickIds).then(({ data }) => ({ type: 'pick', data })))
                        if (collectionIds.length > 0) promises.push(supabase.from('collections').select('id').in('id', collectionIds).then(({ data }) => ({ type: 'collection', data })))
                        if (folderIds.length > 0) promises.push(supabase.from('folders').select('id').in('id', folderIds).then(({ data }) => ({ type: 'folder', data })))

                        const results = await Promise.all(promises)
                        
                        let hasChanges = false
                        let verifiedList = [...parsed]
                        
                        results.forEach(result => {
                            const existingIds = new Set(result.data?.map(item => item.id) || [])
                            const originalLen = verifiedList.length
                            verifiedList = verifiedList.filter(item => {
                                if (item.type !== result.type) return true
                                return existingIds.has(item.id)
                            })
                            if (verifiedList.length !== originalLen) hasChanges = true
                        })

                        if (hasChanges) {
                            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(verifiedList))
                            setRecentSearches(verifiedList)
                        }
                    }
                }
            } catch (err) {
                console.error('Failed to parse recent searches', err)
            }
        }
        
        load()
        
        // Listen for cross-component local updates
        const handleUpdate = () => {
            try {
                const saved = localStorage.getItem(RECENT_SEARCHES_KEY)
                if (saved) setRecentSearches(JSON.parse(saved))
            } catch {}
        }
        window.addEventListener('recent_searches_updated', handleUpdate)
        return () => window.removeEventListener('recent_searches_updated', handleUpdate)
    }, [])

    const addRecentSearch = (item: RecentSearchItem) => {
        setRecentSearches(prev => {
            const filtered = prev.filter(p => p.id !== item.id)
            const updated = [item, ...filtered].slice(0, MAX_RECENT_SEARCHES)
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated))
            return updated
        })
    }
    
    const removeRecentSearch = (id: string) => {
        setRecentSearches(prev => {
            const filtered = prev.filter(p => p.id !== id)
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(filtered))
            return filtered
        })
    }

    const clearRecentSearches = () => {
        setRecentSearches([])
        localStorage.removeItem(RECENT_SEARCHES_KEY)
    }

    return {
        recentSearches,
        addRecentSearch,
        removeRecentSearch,
        clearRecentSearches
    }
}

export function removeRecentSearchItem(id: string) {
    try {
        const saved = localStorage.getItem(RECENT_SEARCHES_KEY)
        if (saved) {
            const parsed = JSON.parse(saved)
            const filtered = parsed.filter((p: any) => p.id !== id)
            localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(filtered))
            window.dispatchEvent(new Event('recent_searches_updated'))
        }
    } catch (err) {
        console.error(err)
    }
}
